/** The deck: what is coming and what is done (UX §8.4).
 *
 * Order is the approved plan's segments in order, each holding its items in plan order, then a
 * divider, then the rest of the course's bank. On top of that: a question that has been asked
 * leaves **Next** and appears at the top of **Asked**, the selection advances to the next
 * unasked card, and skipping is a decision this device makes about its own view — no event, no
 * record, nothing that reaches a student.
 *
 * Pure, so the rules can be tested without a browser (`apps/web/tests/queue.test.ts`).
 */
import type { Item } from "../teacher/api";
import type { AskedEntry, Snapshot } from "./useLive";

export type Segment = { title: string; kind: string; minutes: number; item_ids: string[] };

/** One card in Next: the item, which plan step it belongs to, and whether it opens a group. */
export type QueueRow = {
  item: Item;
  /** Index into the plan's segments, or null once the plan is finished. */
  segment: number | null;
  /** This card is the first of its segment, so the header is drawn above it. */
  first: boolean;
  /** The plan ends above this card and the rest of the bank follows. */
  divider: boolean;
};

export type SegmentFacts = { index: number; title: string; minutes: number; asked: number; total: number };

export type Deck = {
  next: QueueRow[];
  skipped: Item[];
  asked: AskedEntry[];
  segments: SegmentFacts[];
  /** The plan's questions are all asked (or skipped) and the bank follows. */
  planDone: boolean;
};

/** Items in the order the approved plan checks them, then the rest of the bank. */
export function ordered(items: Item[], segments: Segment[] | null): { item: Item; segment: number | null }[] {
  if (!segments || segments.length === 0) return items.map((item) => ({ item, segment: null }));
  const byId = new Map(items.map((it) => [it.item_id, it]));
  const planned: { item: Item; segment: number | null }[] = [];
  const seen = new Set<string>();
  segments.forEach((s, i) => {
    for (const id of s.item_ids) {
      const it = byId.get(id);
      if (it && !seen.has(id)) {
        seen.add(id);
        planned.push({ item: it, segment: i });
      }
    }
  });
  return [...planned, ...items.filter((it) => !seen.has(it.item_id)).map((item) => ({ item, segment: null }))];
}

export function buildDeck(
  items: Item[],
  segments: Segment[] | null,
  asked: AskedEntry[],
  skipped: string[],
  search = "",
): Deck {
  const askedIds = new Set(asked.map((a) => a.item_id));
  const skip = new Set(skipped);
  const all = ordered(items, segments);
  const q = search.trim().toLocaleLowerCase();
  const rows: QueueRow[] = [];
  let lastSegment: number | null | undefined;
  let dividerDrawn = false;
  for (const { item, segment } of all) {
    if (askedIds.has(item.item_id) || skip.has(item.item_id)) continue;
    if (q && !item.stem.toLocaleLowerCase().includes(q)) continue;
    const first = segment !== null && segment !== lastSegment;
    const divider = segment === null && !dividerDrawn && rows.length > 0;
    if (divider) dividerDrawn = true;
    rows.push({ item, segment, first, divider });
    lastSegment = segment;
  }
  const facts: SegmentFacts[] = (segments ?? []).map((s, index) => ({
    index,
    title: s.title,
    minutes: s.minutes,
    total: s.item_ids.length,
    asked: s.item_ids.filter((id) => askedIds.has(id)).length,
  }));
  return {
    next: rows,
    skipped: all.filter(({ item }) => skip.has(item.item_id) && !askedIds.has(item.item_id)).map((r) => r.item),
    asked,
    segments: facts,
    planDone: rows.every((r) => r.segment === null),
  };
}

/** The selection after the deck changes: keep it if the card is still there, otherwise take
 * the card that now occupies its place, otherwise the last one. Never nothing while Next has
 * cards — one card is selected at all times (§8.4). */
export function keepSelection(next: QueueRow[], selected: string | null, previousIndex: number): string | null {
  if (next.length === 0) return null;
  if (selected && next.some((r) => r.item.item_id === selected)) return selected;
  const i = Math.min(Math.max(previousIndex, 0), next.length - 1);
  return next[i]!.item.item_id;
}

export function moveSelection(next: QueueRow[], selected: string | null, delta: number): string | null {
  if (next.length === 0) return null;
  const at = next.findIndex((r) => r.item.item_id === selected);
  const to = Math.min(Math.max((at < 0 ? 0 : at) + delta, 0), next.length - 1);
  return next[to]!.item.item_id;
}

/** The next free attempt for asking this question again: one past the highest round of it
 * this session has seen. Without the snapshot's history the client would have to guess, and a
 * guess of 1 would count the old answers into the new round's bars. */
export function nextAttempt(asked: AskedEntry[], itemId: string): number {
  return asked.reduce((n, a) => (a.item_id === itemId ? Math.max(n, a.attempt + 1) : n), 1);
}

/** Only the newest closed round can be re-voted: the engine's `revote` acts on the current
 * item, and a re-vote is "the same round again, after discussion" — which is only true of the
 * question the teacher just asked. */
export function canRevote(snapshot: Snapshot | null, entry: AskedEntry): boolean {
  const cur = snapshot?.item;
  if (!cur || !cur.closed) return false;
  if (cur.item_id !== entry.item_id || cur.attempt !== entry.attempt) return false;
  return entry.answered > 0;
}

/** A round asked aloud, closed, with answers and no answer of its own: this is the one the
 * teacher can say "the answer was C" about afterwards. */
export function canScore(entry: AskedEntry): boolean {
  return entry.spoken && !!entry.closed_at && entry.answered > 0 && entry.option_keys.length > 0;
}
